import { DollarOutlined } from '@ant-design/icons';
import { XIcon } from '@heroicons/react/solid';
import { ConfigProvider, Input, Radio, theme, ThemeConfig } from 'antd';
import SearchIconSVG from 'assets/Icons/SearchIconSVG';
import AllIcon from 'assets/Icons/Shop/AllIcon';
import BestSellerIcon from 'assets/Icons/Shop/BestSellerIcon';
import BundlesShopIcon from 'assets/Icons/Shop/Bundles';
import NewShopIcon from 'assets/Icons/Shop/NewShopIcon';
import PriceTagIcon from 'assets/Icons/Shop/PriceTagIcon';
import DiscountIcon from 'assets/NotificationIcons/DiscountIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import slugify from 'slugify';

import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { getCoursesAllCategories } from '@/services/Courses';
import { getAllBookCategories } from '@/services/Library/Books';

const config: ThemeConfig = {
  token: { colorPrimary: 'white', fontFamily: 'inherit' },
};

interface Props {
  setSearchKeyword: (value: string) => void;
  searchKeyword: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setOpenDrawer: (value: boolean) => void;
  openDrawer: boolean;
  type?: 'books' | 'courses';
}

const staticCategoriesMap = {
  courses: [
    { name: 'All', icon: <AllIcon />, count: null, route: '/' },
    // {
    //   name: 'New Releases',
    //   icon: <NewShopIcon />,
    //   count: null,
    //   route: '/new-releases',
    // },
    {
      name: 'Bundles',
      icon: <BundlesShopIcon />,
      count: null,
      badge: 'Value for Money',
      route: '/bundles',
    },
  ],
  default: [
    { name: 'All', icon: <AllIcon />, count: null, route: '/' },
    {
      name: 'Free only',
      icon: <PriceTagIcon />,
      count: null,
      route: '/free-only',
    },
    {
      name: 'Paid only',
      icon: <DollarOutlined />,
      count: null,
      route: '/paid-only',
    },
    {
      name: 'Bestsellers',
      icon: <BestSellerIcon />,
      count: null,
      route: '/bestsellers',
    },
    {
      name: 'New Releases',
      icon: <NewShopIcon />,
      count: null,
      route: '/new-releases',
    },
    {
      name: 'Bundles',
      icon: <BundlesShopIcon />,
      count: null,
      badge: 'Value for Money',
      route: '/bundles',
    },
  ],
};

const CategoriesSidebar: React.FC<Props> = ({
  searchKeyword,
  handleInputChange,
  setSearchKeyword,
  setOpenDrawer,
  type = 'books',
}) => {
  const [categories, setCategories] = useState<
    { id: string; name: string; count: string }[]
  >([]);
  const router = useRouter();
  const staticCategories =
    staticCategoriesMap[type as keyof typeof staticCategoriesMap] ||
    staticCategoriesMap.default;

  const categoryName =
    router.asPath?.split('?')[0]?.split('/').filter(Boolean).at(-1) ?? null;

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const handleGetCategories = async () => {
    try {
      if (type === 'books') {
        const response = await getAllBookCategories();

        const { data } = response;

        setCategories(
          (data ?? response)
            .filter((category: any) => category.bookCount >= 1)
            .map((category: any) => ({
              id: category?._id,
              name: category?.name,
              count: category?.bookCount,
            })),
        );
      } else {
        const response = await getCoursesAllCategories();
        const { data } = response;

        setCategories(data);
      }
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    }
  };

  useEffect(() => {
    handleGetCategories();
  }, []);

  const item = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0 },
  };

  const slugifyName = (name: string) =>
    slugify(name, { lower: true, strict: true });

  return (
    <ConfigProvider theme={{ ...config, algorithm: [theme.darkAlgorithm] }}>
      <motion.aside
        className='w-full p-4 lg:p-0 xl:w-[320px]  '
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <div className='relative mb-3  hidden md:block'>
            <Input
              size='small'
              placeholder={`Search for ${type === 'books' ? 'Books' : 'Courses'}`}
              className='text-sm rounded-[10px] w-full focus-within:bg-bgColor border-[1.5px]  placeholder:text-textDarkGrayColor py-1 px-3 text-white h-10 lg:h-10 bg-bgColor customInputBlack makeBgBlack'
              value={searchKeyword}
              style={{
                backgroundColor: '#000000',
              }}
              onChange={handleInputChange}
              prefix={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className='text-textLightGray'
                >
                  <SearchIconSVG width='16' isGray={true} />
                </motion.div>
              }
              suffix={
                searchKeyword?.length && (
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <XIcon
                      onClick={() => {
                        setSearchKeyword('');
                      }}
                      className='w-5 h-5 pr-1 text-[#A6A6A6] cursor-pointer'
                    />
                  </motion.div>
                )
              }
            />
          </div>

          <motion.div
            className='space-y-0.5'
            variants={container}
            initial='hidden'
            animate='show'
          >
            {staticCategories?.map((category) => (
              <motion.div key={category.name} variants={item}>
                <button
                  className={clsx(
                    `flex items-center customRadioButton w-full py-[9px]  gap-x-2 px-3.5 rounded-[10px] hover:bg-[#121212] transition-colors `,
                    category.route?.includes(categoryName || '') &&
                      'bg-[#282829]',
                  )}
                  onClick={() => {
                    router.push(
                      `/${type}/shop/${category?.route?.toLowerCase()}`,
                    );
                    setOpenDrawer(false);
                  }}
                >
                  <div className='flex '>
                    <span className='flex items-center justify-center w-5 h-5 text-gray-400'>
                      {category.icon}
                    </span>
                    <span className='ml-2 text-sm text-textLightGray'>
                      {category.name}
                    </span>
                  </div>

                  {category.badge && (
                    <span className='flex text-xs items-center justify-center gap-x-1 px-2 py-0.5 bg-dropDownMenuBg border border-[#FFFFFF1A] text-green-400 rounded-full'>
                      <DiscountIcon width='12' /> {category.badge}
                    </span>
                  )}
                </button>
              </motion.div>
            ))}
          </motion.div>

          <div className='pt-3 mt-3 border-t border-[#282829]'>
            <h3 className='text-xs font-medium text-textLightGray mb-3 uppercase tracking-wide'>
              Categories
            </h3>

            <motion.div
              className='space-y-0.5'
              variants={container}
              initial='hidden'
              animate='show'
            >
              {categories.map((category, index) => {
                const isSelected =
                  slugifyName(category.name)?.toLowerCase() ===
                  categoryName?.toLowerCase();
                return (
                  <motion.div key={`${category.name}-${index}`} variants={item}>
                    <button
                      className={clsx(
                        `flex items-center customRadioButton w-full py-[9px]  px-3.5 rounded-[10px] hover:bg-[#121212] transition-colors `,
                        isSelected && 'bg-[#282829]',
                      )}
                      onClick={() => {
                        const slug = slugify(category?.name || '', {
                          lower: true,
                        });
                        router.push(`/${type}/shop/${slug}`);
                        setOpenDrawer(false);
                      }}
                    >
                      <Radio
                        className='customRadioButton '
                        checked={isSelected}
                        style={{
                          borderColor: '#FF5612',
                          color: '#FF5612',
                        }}
                      />
                      <span
                        className={`ml-1 text-sm ${isSelected ? 'text-white' : 'text-textLightGray'}`}
                      >
                        {category.name}
                      </span>
                      {category?.count && isSelected && (
                        <span className='ml-auto text-sm text-[#F9F9FA]'>
                          {category?.count}
                        </span>
                      )}
                    </button>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </motion.aside>
    </ConfigProvider>
  );
};
export default CategoriesSidebar;
